<template>
  <div class="element-properties">
    <v-form>
      <!-- Common Properties -->
      <div class="property-section">
        <h4 class="text-subtitle-2 mb-2">General</h4>

        <v-text-field
          v-model="localElement.label"
          label="Label"
          variant="outlined"
          density="compact"
          @update:model-value="updateElement"
        />

        <v-text-field
          v-model="localElement.canonicalName"
          label="Canonical Name"
          variant="outlined"
          density="compact"
          @update:model-value="updateElement"
        />

        <v-text-field
          v-model="localElement.class"
          label="CSS Classes"
          variant="outlined"
          density="compact"
          placeholder="col-xs-12 col-sm-6 col-md-4 col-lg-3"
          @update:model-value="updateElement"
        />
      </div>

      <!-- Grid Helper -->
      <div class="property-section">
        <h4 class="text-subtitle-2 mb-2">Grid Layout</h4>

        <v-row
          v-for="(rule, index) in gridRules"
          :key="index"
          class="align-center mt-n2"
        >
          <v-col cols="12" class="d-flex align-center">
            <span class="mr-2">col-</span>

            <!-- Size dropdown -->
            <v-select
              v-model="rule.size"
              :items="availableSizesForGrid(rule.size)"
              variant="outlined"
              density="compact"
              hide-details
              class="mr-2"
              @update:model-value="updateGridClasses"
            />

            <!-- Number dropdown -->
            -
            <v-select
              v-model="rule.number"
              :items="gridNumberOptions"
              variant="outlined"
              density="compact"
              hide-details
              class="ml-2"
              @update:model-value="updateGridClasses"
            />

            <!-- Remove breakpoint -->
            <v-btn
              icon="mdi-close"
              size="small"
              color="error"
              variant="text"
              @click="removeRule(index)"
            />
          </v-col>
        </v-row>

        <!-- Add new breakpoint -->
        <v-btn
          size="small"
          color="primary"
          variant="outlined"
          prepend-icon="mdi-plus"
          @click="addRule"
          :disabled="!canAddGridRule"
        >
          Add Breakpoint
        </v-btn>

        <!-- Offset Controls -->
        <h5 class="text-subtitle-2 mb-2 mt-6">Offset Classes</h5>

        <v-row
          v-for="(rule, index) in offsetRules"
          :key="'offset-' + index"
          class="align-center mt-n2"
        >
          <v-col cols="12" class="d-flex align-center">
            <span class="mr-2">offset-</span>

            <!-- Size dropdown -->
            <v-select
              v-model="rule.size"
              :items="availableSizesForOffset(rule.size)"
              variant="outlined"
              density="compact"
              hide-details
              class="mr-2"
              @update:model-value="updateGridClasses"
            />

            <!-- Number dropdown -->
            -
            <v-select
              v-model="rule.number"
              :items="offsetNumberOptions"
              variant="outlined"
              density="compact"
              hide-details
              class="ml-2"
              @update:model-value="updateGridClasses"
            />

            <!-- Remove breakpoint -->
            <v-btn
              icon="mdi-close"
              size="small"
              color="error"
              variant="text"
              @click="removeOffsetRule(index)"
            />
          </v-col>
        </v-row>

        <!-- Add new offset -->
        <v-btn
          size="small"
          color="primary"
          variant="outlined"
          prepend-icon="mdi-plus"
          @click="addOffsetRule"
          :disabled="!canAddOffsetRule"
        >
          Add Offset
        </v-btn>
      </div>

      <!-- Input-specific Properties -->
      <div v-if="localElement.type === 'input'" class="property-section">
        <h4 class="text-subtitle-2 mb-2">Input Settings</h4>

        <v-select
          v-model="(localElement as InputElement).inputType"
          :items="inputTypeOptions"
          label="Input Type"
          variant="outlined"
          density="compact"
          @update:model-value="updateElement"
        />

        <v-text-field
          v-model="(localElement as InputElement).canonicalName"
          label="Property Name"
          variant="outlined"
          density="compact"
          placeholder="e.g., buildingStaticPressureSetpoint"
          @update:model-value="updateElement"
        />

        <v-text-field
          v-model="(localElement as InputElement).placeholder"
          label="Placeholder"
          variant="outlined"
          density="compact"
          @update:model-value="updateElement"
        />
      </div>

      <!-- Big Label Properties -->
      <div
        v-if="localElement.type === 'bigTextSmallLabel'"
        class="property-section"
      >
        <h4 class="text-subtitle-2 mb-2">Label Settings</h4>

        <v-text-field
          v-model="(localElement as BigLabelElement).canonicalName"
          label="Property Name"
          variant="outlined"
          density="compact"
          @update:model-value="updateElement"
        />

        <!-- Child Label Management -->
        <div class="mt-4">
          <h5 class="text-subtitle-2 mb-2">Child Label (Optional)</h5>

          <v-switch
            :model-value="!!(localElement as BigLabelElement).children"
            label="Add Child Label"
            inset
            hide-details
            color="primary"
            @update:model-value="toggleChildLabel"
          />

          <template v-if="(localElement as BigLabelElement).children">
            <v-text-field
              v-model="(localElement as BigLabelElement).children!.label"
              label="Child Label Text"
              variant="outlined"
              density="compact"
              placeholder="e.g., Temperature (°F)"
              class="mt-2"
              @update:model-value="updateElement"
            />

            <v-text-field
              v-model="(localElement as BigLabelElement).children!.canonicalName"
              label="Child Canonical Name"
              variant="outlined"
              density="compact"
              class="mt-2"
              @update:model-value="updateElement"
            />
          </template>
        </div>
      </div>

      <!-- Group Properties -->
      <div v-if="localElement.type === 'group'" class="property-section">
        <h4 class="text-subtitle-2 mb-2">Group Settings</h4>
        <p class="text-caption text-disabled">
          Groups can contain other elements. Drag elements into the group to add
          them.
        </p>
      </div>
    </v-form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import {
  LayoutElement,
  InputElement,
  BigLabelElement,
  InputType,
} from '@/Core.Service.Domain/Controls/AHUs/types/AHULayout';

interface Props {
  element: LayoutElement;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  update: [element: LayoutElement];
  delete: [elementId: string];
}>();

// Local copy of element for editing
const localElement = ref<LayoutElement>({ ...props.element });

const sizeOptions = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];
const gridNumberOptions = Array.from({ length: 12 }, (_, i) =>
  (i + 1).toString()
);
const offsetNumberOptions = Array.from({ length: 12 }, (_, i) =>
  (i + 1).toString()
);

const gridRules = ref<{ size: string; number: string }[]>([
  { size: 'sm', number: '12' },
]);

const offsetRules = ref<{ size: string; number: string }[]>([]);

const availableSizesForGrid = (currentSize: string) => {
  const used = gridRules.value.map((r) => r.size);
  return sizeOptions.filter((s) => !used.includes(s) || s === currentSize);
};

const availableSizesForOffset = (currentSize: string) => {
  const used = offsetRules.value.map((r) => r.size);
  return sizeOptions.filter((s) => !used.includes(s) || s === currentSize);
};

const addRule = () => {
  const used = gridRules.value.map((r) => r.size);
  const available = sizeOptions.filter((s) => !used.includes(s));
  if (available.length === 0) return;
  gridRules.value.push({ size: available[0], number: '6' });
  updateGridClasses();
};

const addOffsetRule = () => {
  const used = offsetRules.value.map((r) => r.size);
  const available = sizeOptions.filter((s) => !used.includes(s));
  if (available.length === 0) return;
  offsetRules.value.push({ size: available[0], number: '1' });
  updateGridClasses();
};

const removeRule = (index: number) => {
  gridRules.value.splice(index, 1);
  updateGridClasses();
};

const removeOffsetRule = (index: number) => {
  offsetRules.value.splice(index, 1);
  updateGridClasses();
};

// Disable Add button if no more sizes left
const canAddGridRule = computed(() => {
  const used = gridRules.value.map((r) => r.size);
  return sizeOptions.some((s) => !used.includes(s));
});

const canAddOffsetRule = computed(() => {
  const used = offsetRules.value.map((r) => r.size);
  return sizeOptions.some((s) => !used.includes(s));
});

const inputTypeOptions: { title: string; value: InputType }[] = [
  { title: 'Text', value: 'text' },
  { title: 'Number', value: 'number' },
  { title: 'Date', value: 'date' },
  { title: 'Email', value: 'email' },
  { title: 'Password', value: 'password' },
  { title: 'Big Centered Label', value: 'big-centered-label' },
  { title: 'Red/Green Circle', value: 'red-green-circle' },
];

// Update grid classes
const updateGridClasses = () => {
  let classes = '';

  // Add col-span rules
  for (const rule of gridRules.value) {
    classes += `col-${rule.size}-${rule.number} `;
  }

  // Add offset rules (only if toggle is enabled)
  for (const rule of offsetRules.value) {
    classes += `offset-${rule.size}-${rule.number} `;
  }

  localElement.value.class = classes.trim();
  updateElement();
};

// Update element
const updateElement = () => {
  emit('update', localElement.value);
};

// Toggle child label for BigLabelElement
const toggleChildLabel = (hasChild: boolean | null) => {
  if (localElement.value.type === 'bigTextSmallLabel' && hasChild !== null) {
    const bigLabel = localElement.value as BigLabelElement;
    if (hasChild) {
      // Create a new child label
      bigLabel.children = {
        id: `child_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        type: 'regGreenLabel',
        label: '',
        canonicalName: '',
      };
    } else {
      // Remove the child label
      bigLabel.children = undefined;
    }
    updateElement();
  }
};

// Parse existing classes from element
const parseExistingClasses = (element: LayoutElement) => {
  const classes = element.class || '';

  // Reset rules
  gridRules.value = [];
  offsetRules.value = [];

  // Parse grid classes (col-size-number pattern)
  const gridMatches = classes.match(/col-(xs|sm|md|lg|xl|xxl)-(\d+)/g);
  if (gridMatches) {
    for (const match of gridMatches) {
      const parts = match.split('-');
      if (parts.length === 3) {
        const size = parts[1];
        const number = parts[2];
        gridRules.value.push({ size, number });
      }
    }
  }

  // Parse offset classes (offset-size-number pattern)
  const offsetMatches = classes.match(/offset-(xs|sm|md|lg|xl|xxl)-(\d+)/g);
  if (offsetMatches) {
    for (const match of offsetMatches) {
      const parts = match.split('-');
      if (parts.length === 3) {
        const size = parts[1];
        const number = parts[2];
        offsetRules.value.push({ size, number });
      }
    }
  }

  // If no grid rules found, add a default one
  if (gridRules.value.length === 0) {
    gridRules.value.push({ size: 'sm', number: '12' });
  }
};

// Watch for external element changes
watch(
  () => props.element,
  (newElement: LayoutElement) => {
    localElement.value = { ...newElement };
    parseExistingClasses(newElement);
  },
  { immediate: true, deep: true }
);
</script>

<style lang="scss" scoped>
.element-properties {
  padding: 0 8px 0 0; // Reserve space for scrollbar
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;

  // Hide scrollbar by default
  &::-webkit-scrollbar {
    width: 8px; // Keep consistent width
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent; // Hide thumb by default
    border-radius: 4px;
  }

  // Show scrollbar on hover
  &:hover {
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;

      &:hover {
        background: #a8a8a8;
      }
    }
  }
}

.property-section {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }

  h4 {
    color: #1976d2;
    margin-bottom: 12px;
  }
}

.v-text-field,
.v-select {
  margin-bottom: 8px;
}
</style>
